import { MentionData } from '@draft-js-plugins/mention'

import { useGetLoggedUserQuery, useGetStaffUsersQuery } from '@/services/user'
import { StreamingNote } from '@/types/unlock'
import { FEATURES_LIST } from '@/utils/features/featuresList'
import { useFeatureFlag } from '@/utils/features/splitSdk'
import useShouldHideDTI from '@/utils/useShouldHideDTI'
import {
  getUserFullNameOrEmail,
  staffUserGroupsAvailableInStreamingNotes,
  staffUserGroupsAvailableInWorkviews,
} from '@/utils/user'

export function useStreamingNotes() {
  const addEscalationEnabled = useFeatureFlag(FEATURES_LIST.ADD_ESCALATION_DRAWER)
  const { data: users } = useGetStaffUsersQuery()
  const { data: loggedUser } = useGetLoggedUserQuery()
  const DELETED_MESSAGE_TEXT = 'This message was deleted.'

  const shouldHideDTI = useShouldHideDTI()

  const rawMentions: MentionData[] = users
    ?.filter((user) => user.email && user.is_active)
    .filter((user) =>
      user.groups.some((group) =>
        [
          ...staffUserGroupsAvailableInStreamingNotes,
          ...staffUserGroupsAvailableInWorkviews,
        ].includes(group.name)
      )
    )
    .sort((a, b) => a.first_name.localeCompare(b.first_name))
    .map((user) => ({
      id: user.data.id,
      email: user.email,
      name: getUserFullNameOrEmail(user),
    }))

  const mentions = addEscalationEnabled
    ? rawMentions
        ?.filter((user) => user.email !== 'avm_value_adjustment@unlock.com')
        ?.filter((user) => !(shouldHideDTI && user.email === 'dti_review@unlock.com'))
    : rawMentions

  /**
   * @deprecated Do not use because it uses the id of UserData instead of the id of User
   * @use isNoteFromCurrentUser instead
   * */
  const isMessageFromCurrentUser = (sn: StreamingNote) => loggedUser?.data?.id === sn.user.id

  const isNoteFromCurrentUser = (sn: StreamingNote) => loggedUser?.data?.id === sn.user.id

  const isNoteDeletable = (sn: StreamingNote) => {
    let deletable = true
    if (!sn.child_streaming_notes) {
      return deletable
    }
    sn.child_streaming_notes.forEach((child) => {
      if (child.user.id !== loggedUser.data.id) {
        deletable = false
      }
    })
    return deletable
  }
  const isNoteDeleted = (sn: StreamingNote) => sn.text === DELETED_MESSAGE_TEXT

  const isMessageSeenByCurrentUser = (sn: StreamingNote) => sn.seen_by.includes(loggedUser.data.id)

  const getUserById = (id: number) => users?.find((user) => user.data.id === id)

  return {
    DELETED_MESSAGE_TEXT,
    mentions,
    isMessageFromCurrentUser,
    isNoteFromCurrentUser,
    isNoteDeletable,
    isNoteDeleted,
    isMessageSeenByCurrentUser,
    getUserById,
    loggedUser,
  }
}
